/**
 * Tipos do domínio de Consulta (M7) no frontend. Espelham o contrato congelado
 * das respostas da API (listas com {data, nextCursor}; ficha/pastas no topo).
 * Nenhum dado pessoal é logado no cliente (RN-M11-001).
 */

export type Marcador = 'OK' | 'CORRIGIDO' | 'ALERTA' | 'ERRO';

/**
 * Nível de um achado (ADR-035): os marcadores e `OBSERVACAO` — o que foi lido com segurança
 * e não bate com a regra ou a base. É nível só de achado: o campo e a visita contam como OK.
 */
export type NivelAchado = Marcador | 'OBSERVACAO';

/**
 * A evidência da visita (ADR-038): `imagem` = termo digitalizado ("Com imagem"); `planilha` = registrada pela
 * planilha de instalações, sem imagem ("Planilha (sem imagem)").
 */
export type Evidencia = 'imagem' | 'planilha';

/** ADR-046: de onde veio a visita — o termo, ou a visita sem cópia da planilha de instalações, do RF ou da base de controle. */
export type FonteVisita = 'termo' | 'instalacoes' | 'relatorio_fotografico' | 'controle';

export interface EscolaListItem {
  readonly inep: string | null;
  readonly nome: string;
  /** Nome como lido no termo (ADR-035), da visita mais recente que o tem. Ausente numa API anterior. */
  readonly nomeTermo?: string | null;
  /** O nome do município pela base IBGE ativa (sem o nome na base, o código) — spec escolas-ordenacao-e-formatos. */
  readonly municipio: string | null;
  /** O código IBGE do município (null no grupo "não identificada"; ausente numa API anterior). */
  readonly municipioIbge?: string | null;
  readonly uf: string | null;
  readonly qtdVisitas: number;
  readonly ultimaVisita: string | null;
  readonly ultimoResultado: string | null;
  readonly temSuspeito: boolean;
  /** ADR-038: `imagem` se a escola tem alguma visita com imagem, senão `planilha` (null sem visitas; ausente numa API anterior). */
  readonly evidencia?: Evidencia | null;
}

export interface Coordenada {
  readonly lat: number | null;
  readonly lng: number | null;
}

export interface VisitaTimeline {
  readonly id: string;
  readonly dataVisita: string | null;
  readonly resultado: string | null;
  readonly marcador: Marcador;
  readonly classificacao: string;
  /** ADR-038: a evidência da visita e se é a linha de referência (ausentes numa API anterior). */
  readonly evidencia?: Evidencia;
  readonly referencia?: boolean;
  /** ADR-046 (ausente numa API anterior). */
  readonly fonte?: FonteVisita;
}

/** ADR-046: uma linha da base de controle da escola, com os campos mapeados. */
export interface LinhaControle {
  readonly arquivo: string;
  readonly aba: string;
  readonly linha: number;
  readonly tipo: 'controle' | 'designacao';
  readonly data: string | null;
  readonly situacao: 'produtiva' | 'improdutiva' | 'desativada' | 'nao_realizada' | 'desconhecida';
  readonly statusOriginal: string | null;
  readonly motivo: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly downMbps: number | null;
  readonly upMbps: number | null;
  readonly fase: string | null;
  readonly lote: string | null;
  readonly fornecedor: string | null;
  readonly designacao: string | null;
  readonly visitaId: string | null;
}

export interface EscolaFicha {
  readonly inep: string;
  readonly nome: string;
  /** Nome como lido no termo (ADR-035), como em `EscolaListItem`. Ausente numa API anterior. */
  readonly nomeTermo?: string | null;
  readonly municipio: string | null;
  readonly uf: string | null;
  /** SEMPRE objeto (contrato); lat/lng null quando não há geometria. */
  readonly coordenada: Coordenada;
  readonly visitas: readonly VisitaTimeline[];
  readonly outrosDocumentos: readonly VisitaTimeline[];
  /** ADR-046: "Nas planilhas de controle" (ausente numa API anterior). */
  readonly nasPlanilhasDeControle?: readonly LinhaControle[];
}

export interface VisitaListItem {
  readonly id: string;
  readonly escolaInep: string | null;
  /** Nome oficial do INEP; nulo quando o INEP não foi resolvido no catálogo. */
  readonly nomeEscola: string | null;
  /** Nome como lido no termo (ADR-035). Ausente numa API anterior. */
  readonly nomeEscolaTermo?: string | null;
  readonly municipio: string | null;
  readonly uf: string | null;
  readonly dataVisita: string | null;
  readonly resultado: string | null;
  readonly marcador: Marcador;
  /** Nulos na visita sem imagem (ADR-038 — não há arquivo no acervo). */
  readonly nomeArquivo: string | null;
  readonly caminhoOriginal: string | null;
  readonly tipoDocumento: string | null;
  readonly conferida: boolean;
  readonly rowVersion: number;
  /** ADR-038: `imagem` (termo digitalizado) ou `planilha` (sem imagem); `referencia` = a escola tem termo com imagem. */
  readonly evidencia?: Evidencia;
  readonly referencia?: boolean;
  /** ADR-046 (ausente numa API anterior). */
  readonly fonte?: FonteVisita;
}

/**
 * Resultado de UMA regra do motor sobre UM campo (ADR-030): o que foi conferido ou
 * corrigido e por quê. A mensagem nunca traz o valor pessoal completo.
 */
export interface AchadoCampo {
  readonly regra: string;
  readonly nivel: NivelAchado;
  readonly mensagem: string;
  /**
   * ADR-048: a evidência complementar que o achado cita (o `id` de `VisitaFicha.evidenciasComplementares`) — só quando
   * a evidência é uma linha da tabela; ausente nos outros achados e numa API anterior.
   */
  readonly evidenciaId?: string;
}

export interface CampoFicha {
  readonly nomeCampo: string;
  readonly valorLiteral: string | null;
  readonly valorFinal: string | null;
  readonly marcador: Marcador;
  readonly confianca: number | null;
  readonly editadoPorUsuario: boolean;
  /** Sempre array na API atual; ausente numa API anterior aos achados (tratar como `[]`). */
  readonly achados?: readonly AchadoCampo[];
}

/** A escola da visita (ADR-035): o INEP resolvido, o nome oficial e o nome como lido no termo. */
export interface EscolaDaVisita {
  readonly inep: string | null;
  readonly nomeOficial: string | null;
  readonly nomeTermo: string | null;
}

export interface VisitaFicha {
  readonly id: string;
  /** snake_case por exigência do contrato (GET /visitas/{id}). */
  readonly row_version: number;
  readonly inep: string | null;
  /** Ausente numa API anterior (ADR-035). */
  readonly escola?: EscolaDaVisita;
  /** Nulo na visita sem imagem (ADR-038). */
  readonly caminhoOriginal: string | null;
  readonly caminhosDuplicados: readonly string[];
  /** Página do arquivo multipágina (ADR-033); null = imagem única. Ausente numa API anterior. */
  readonly pagina?: number | null;
  /** Total de páginas do arquivo (ADR-033); null = imagem única. */
  readonly paginasArquivo?: number | null;
  readonly sha256: string;
  readonly s3Key: string | null;
  readonly classificacao: string;
  readonly marcador: Marcador;
  readonly campos: readonly CampoFicha[];
  readonly conferida: boolean;
  readonly visualizadorUrl: string | null;
  /** ADR-036: o reprocessamento mais recente que incluiu a visita; null se nunca houve (ausente numa API anterior). */
  readonly ultimoReprocessamento?: UltimoReprocessamento | null;
  /** ADR-038: a evidência (ausente numa API anterior = com imagem). */
  readonly evidencia?: Evidencia;
  /** ADR-038: na visita sem imagem, a origem na planilha (sem valores de célula); null na com imagem. */
  readonly planilha?: OrigemPlanilha | null;
  /** ADR-038: na visita com imagem cuja escola também consta da planilha, a linha de referência. */
  readonly planilhaReferencia?: { readonly visitaId: string } | null;
  /** ADR-046: o Relatório Fotográfico da escola (a capa e as fotos); null sem RF; ausente numa API anterior. */
  readonly relatorioFotografico?: RelatorioFotografico | null;
  /** ADR-048: as evidências complementares ligadas à visita ("Outras evidências"); ausente numa API anterior. */
  readonly evidenciasComplementares?: readonly EvidenciaComplementar[];
}

/**
 * Como a evidência complementar foi ligada à visita (ADR-048, design §3): `proprio` = o carimbo impresso no próprio
 * termo; `inep_caminho` = o INEP no nome do arquivo ou da pasta; `pasta` = a mesma pasta do termo; `inep_lido` = o INEP
 * lido na evidência; `citada` = a escola citada num ofício.
 */
export type CriterioLigacao = 'proprio' | 'inep_caminho' | 'pasta' | 'inep_lido' | 'citada';

/**
 * ADR-048: uma evidência complementar da visita — documento do acervo que não é o termo (teste de velocidade, app de
 * GPS, ofício…). No código, sempre "evidência complementar": `Evidencia` já nomeia a origem da visita (ADR-038).
 * O `tipo` é a classe da leitura (rótulo em `rotuloTipoDocumento`); `dataHora` vem formatada pela API; `resumo`, o que
 * foi lido (o `campo` é a chave da leitura — rótulo em `rotuloCampoEvidencia`).
 */
export interface EvidenciaComplementar {
  readonly id: string;
  readonly tipo: string;
  readonly criterio: CriterioLigacao;
  readonly dataHora: string | null;
  readonly resumo: readonly { readonly campo: string; readonly valor: string }[];
  readonly caminhoOriginal: string;
  readonly pagina: number | null;
  /** URL pré-assinada da imagem; null quando não deu para gerar (o visualizador mostra o aviso). */
  readonly visualizadorUrl: string | null;
}

/** De onde veio a visita sem cópia (ADR-038): arquivo, aba e linha da planilha; `referencia` = há termo com imagem. */
export interface OrigemPlanilha {
  readonly arquivo: string;
  readonly aba: string;
  readonly linha: number;
  readonly referencia: boolean;
  /** ADR-046 (ausente numa API anterior = planilha de instalações). */
  readonly fonte?: Exclude<FonteVisita, 'termo'>;
}

/** ADR-046: uma foto do Relatório Fotográfico. */
export interface FotoRelatorio {
  readonly aba: string;
  readonly numero: number;
  readonly legenda: string | null;
  readonly caminhoOriginal: string;
  readonly visualizadorUrl: string | null;
}

/** ADR-046: o Relatório Fotográfico da escola na ficha da visita. */
export interface RelatorioFotografico {
  readonly arquivo: string;
  readonly caminhoOriginal: string | null;
  readonly capa: {
    readonly data: string | null;
    readonly latitude: number | null;
    readonly longitude: number | null;
    readonly lidaDe: 'capa' | 'nome_do_arquivo' | 'nenhum';
  };
  readonly fotos: readonly FotoRelatorio[];
  readonly abasSemFoto: readonly string[];
  readonly fotosNaoExtraidas: boolean;
}

/** O último reprocessamento da visita e o que mudou nela (ADR-036) — só nomes de campo. */
export interface UltimoReprocessamento {
  readonly reprocessamentoId: string;
  readonly modo: 'revalidar' | 'reler';
  readonly status: 'enfileirado' | 'em_execucao' | 'concluido' | 'falha';
  readonly criadoEm: string;
  /** Nulo enquanto a visita não foi gravada por ele. */
  readonly resultado: {
    readonly marcadorAntes: Marcador | null;
    readonly marcadorDepois: Marcador;
    readonly camposAlterados: readonly string[];
    readonly gravadoEm: string;
  } | null;
}

export interface PastaNo {
  readonly nome: string;
  readonly caminho: string;
  readonly qtdArquivos: number;
}

export interface ArquivoNo {
  /** A visita do arquivo quando há exatamente uma (um PDF pode ter várias — ADR-033). */
  readonly visitaId: string | null;
  readonly nome: string;
  readonly caminhoOriginal: string;
  readonly tipoDocumento: string | null;
  /** Com várias visitas, o pior marcador delas. */
  readonly marcador: Marcador | null;
  readonly temVisita: boolean;
  /** Visitas geradas pelo arquivo (ADR-033). Ausente numa API anterior. */
  readonly visitas?: number;
  /** Documentos do arquivo: as páginas (multipágina) ou 1; 0 no compactado (ADR-043). */
  readonly documentos?: number;
  /** ADR-043: só no compactado (`tipoDocumento: 'COMPACTADO'`) — quantos arquivos saíram dele (0 enquanto extrai). */
  readonly extraidos?: number;
  /** Requisito 6.2: lido, sem visita, e nenhum documento é termo — a coluna "Visita" diz "não gera visita". */
  readonly naoGeraVisita?: boolean;
  /**
   * ADR-048: na evidência complementar (`tipoDocumento` é uma classe de evidência), a visita a que está ligada; null sem
   * ligação (e nos outros arquivos). Ausente numa API anterior.
   */
  readonly visitaLigadaId?: string | null;
}

export interface Pastas {
  readonly caminho: string;
  readonly pastas: readonly PastaNo[];
  readonly arquivos: readonly ArquivoNo[];
}

export interface DownloadOriginal {
  readonly downloadUrl: string;
  readonly expiresIn: number;
}

/** Filtros de visitas (todos opcionais). */
export interface VisitaFiltros {
  readonly uf?: string;
  readonly municipioIbge?: string;
  readonly escolaInep?: string;
  readonly de?: string;
  readonly ate?: string;
  readonly resultado?: string;
  readonly tipoInstalacao?: string;
  readonly contratada?: string;
  readonly tecnico?: string;
  readonly imei?: string;
  readonly iccid?: string;
  readonly cnpj?: string;
  readonly marcadores?: Marcador;
  /** Só visitas com observação em campo principal (ADR-035) — vai como `observacoes=1`. */
  readonly observacoes?: boolean;
  /** Spec ajustes-telas-amostra: só as visitas com outras evidências ligadas — vai como `outrasEvidencias=1`. */
  readonly outrasEvidencias?: boolean;
  readonly tipoDocumento?: string;
  readonly pasta?: string;
  readonly q?: string;
  /** ADR-036: visitas dos itens `ok` do lote. */
  readonly lote?: string;
  /** ADR-036: visitas do conjunto do reprocessamento. */
  readonly reprocessamento?: string;
  /** ADR-036 (com `reprocessamento`): só as que mudaram — vai como `mudou=1`. */
  readonly mudou?: boolean;
  /** ADR-036: lidas com prompt antigo ou conferidas com regras/bases/parâmetros de antes. */
  readonly pendencia?: PendenciaVisita;
  /** ADR-038: só com imagem ou só da planilha (sem imagem). */
  readonly evidencia?: Evidencia;
  /** ADR-038: inclui as linhas de referência — vai como `incluirReferencias=1`. */
  readonly incluirReferencias?: boolean;
  /** Paginação por cursor (contrato): `cursor` = valor de nextCursor. */
  readonly cursor?: string;
  readonly limit?: number;
}

/** Pendências de reprocessamento (ADR-036). */
export type PendenciaVisita = 'prompt_antigo' | 'regras_antigas';

/**
 * Os filtros da lista na forma de query (ADR-036): o mesmo critério vai à estimativa e ao `POST /reprocessamentos`
 * (`filtroVisitas`) — o conjunto reprocessado é exatamente o da tela.
 */
export type FiltroVisitasQuery = Readonly<Partial<Record<FiltroDeVisita, string>>>;

/** Chaves de filtro de GET /visitas (sem paginação). */
export type FiltroDeVisita = Exclude<keyof VisitaFiltros, 'cursor' | 'limit'>;

/** Filtros de escolas. */
export interface EscolaFiltros {
  readonly uf?: string;
  readonly municipio?: string;
  readonly q?: string;
  readonly suspeitas?: boolean;
  readonly grupo?: 'nao_identificada';
  /** ADR-038: escolas com alguma visita com imagem / escolas só com planilha. */
  readonly evidencia?: Evidencia;
  /** ADR-041 (RN-M7-005): só as escolas com visita associada — vai à API como `comVisitas=1`. */
  readonly comVisitas?: boolean;
  /** Spec escolas-ordenacao-e-formatos: a coluna e a direção (ausentes = o padrão da API). */
  readonly ordenarPor?: OrdenacaoEscolas;
  readonly ordem?: Ordem;
  readonly cursor?: string;
  readonly limit?: number;
}

/** As colunas ordenáveis da lista de escolas — as mesmas do backend (`infra/src/payloads/m7.ts`). */
export const ORDENACOES_ESCOLAS = ['inep', 'nome', 'municipio', 'visitas', 'evidencia', 'ultimaVisita', 'suspeito'] as const;
export type OrdenacaoEscolas = (typeof ORDENACOES_ESCOLAS)[number];
/** As colunas que ordenam por um agregado das visitas (só com "Só escolas com visitas", suspeitas ou evidência). */
export const ORDENACOES_DAS_VISITAS: readonly OrdenacaoEscolas[] = ['visitas', 'evidencia', 'ultimaVisita', 'suspeito'];
/** As colunas que ordenam o grupo "não identificada". */
export const ORDENACOES_NAO_IDENTIFICADA: readonly OrdenacaoEscolas[] = ['nome', 'visitas', 'ultimaVisita', 'suspeito'];
export type Ordem = 'asc' | 'desc';

export interface PaginaLista<T> {
  readonly itens: readonly T[];
  readonly nextCursor: string | null;
  /** ADR-036: total do filtro aplicado (GET /visitas) — "Reprocessar estas N visitas". */
  readonly total?: number;
}
